/**
 * Two-language UI: English + Thai.
 *
 * `t('key')` resolves against the active language from settings. Params use
 * {name} placeholders. Turn instructions themselves are NOT here - Mapbox
 * generates those in Thai when we request language=th, and the voice reads
 * them with a Thai TTS voice.
 */

import { loadSettings } from './settings';

export type Lang = 'en' | 'th';

const STRINGS: Record<string, { en: string; th: string }> = {
  /* login */
  'login.signin': { en: 'Sign in to navigate', th: 'เข้าสู่ระบบเพื่อนำทาง' },
  'login.request': { en: 'Request access', th: 'ขอสิทธิ์เข้าใช้' },
  'login.name': { en: 'Name', th: 'ชื่อ' },
  'login.name.ph': { en: 'Your name', th: 'ชื่อของคุณ' },
  'login.pin': { en: 'PIN', th: 'รหัส PIN' },
  'login.pin.ph': { en: 'Your PIN', th: 'รหัส PIN ของคุณ' },
  'login.pin.new.ph': {
    en: 'Choose a PIN (6+ digits recommended)',
    th: 'ตั้งรหัส PIN (แนะนำ 6 หลักขึ้นไป)',
  },
  'login.submit': { en: 'Sign in', th: 'เข้าสู่ระบบ' },
  'login.wait': { en: 'Please wait…', th: 'กรุณารอสักครู่…' },
  'login.toSignup': { en: 'New driver? Request access', th: 'คนขับใหม่? ขอสิทธิ์เข้าใช้' },
  'login.toLogin': { en: 'Have an account? Sign in', th: 'มีบัญชีแล้ว? เข้าสู่ระบบ' },
  'login.requested': {
    en: 'Access requested. The admin has to approve your account before you can sign in - ask them, then sign in here.',
    th: 'ส่งคำขอแล้ว รอแอดมินอนุมัติบัญชีก่อน จึงจะเข้าสู่ระบบได้',
  },
  'login.pending': {
    en: 'Your account is still waiting for admin approval.',
    th: 'บัญชีของคุณกำลังรอการอนุมัติจากแอดมิน',
  },
  'login.error': { en: 'Something went wrong.', th: 'เกิดข้อผิดพลาด' },

  /* idle / general */
  'idle.whereTo': { en: 'Where to?', th: 'ไปที่ไหน?' },
  'idle.shared': { en: 'Location shared with the fleet', th: 'แชร์ตำแหน่งให้ทีมแล้ว' },
  'idle.shareFailed': { en: 'Could not share - are you online?', th: 'แชร์ไม่สำเร็จ - ออนไลน์อยู่ไหม?' },
  'idle.noFix': { en: 'No GPS fix yet - try again in a moment.', th: 'ยังไม่พบสัญญาณ GPS - ลองอีกครั้ง' },
  'idle.noFixPin': {
    en: 'No GPS - press and hold the map on the spot to save it.',
    th: 'ไม่มีสัญญาณ GPS - กดค้างบนแผนที่ตรงจุดที่ต้องการเพื่อบันทึก',
  },

  /* search */
  'search.ph': {
    en: 'Search, or paste a Google Maps link',
    th: 'ค้นหา หรือวางลิงก์ Google Maps',
  },
  'search.searching': { en: 'Searching…', th: 'กำลังค้นหา…' },
  'search.noResults': {
    en: 'No results. Try a different spelling or drop a pin on the map.',
    th: 'ไม่พบผลลัพธ์ ลองสะกดใหม่ หรือปักหมุดบนแผนที่',
  },
  'search.failed': { en: 'Search failed. Try again.', th: 'ค้นหาไม่สำเร็จ ลองอีกครั้ง' },

  /* push notifications for fleet messages */
  'push.label': { en: 'Notify me about fleet messages', th: 'แจ้งเตือนข้อความจากทีม' },
  'push.hint': {
    en: 'Get food / fuel / help questions even when the app is closed. Navigation itself still needs the app open.',
    th: 'รับคำถามเรื่องอาหาร น้ำมัน หรือขอความช่วยเหลือ แม้ปิดแอปอยู่ ทั้งนี้การนำทางยังต้องเปิดแอปไว้',
  },
  'push.blocked': {
    en: 'Notifications are blocked for this site. Allow them in your browser settings to get fleet messages.',
    th: 'การแจ้งเตือนถูกบล็อกสำหรับเว็บไซต์นี้ อนุญาตในการตั้งค่าเบราว์เซอร์เพื่อรับข้อความจากทีม',
  },
  'push.unsupported': {
    en: 'This device cannot receive notifications. On iPhone, add the app to the Home Screen first.',
    th: 'อุปกรณ์นี้รับการแจ้งเตือนไม่ได้ หากใช้ iPhone ให้เพิ่มแอปไปที่หน้าจอโฮมก่อน',
  },
  'push.failed': {
    en: 'Could not turn notifications on.',
    th: 'เปิดการแจ้งเตือนไม่สำเร็จ',
  },

  /* folders for saved places - fleet-wide filing, e.g. "Sauna customers" */
  'folder.new': { en: 'New folder', th: 'สร้างโฟลเดอร์ใหม่' },
  'folder.saveInto': { en: 'Folder', th: 'โฟลเดอร์' },
  'folder.newPrompt': { en: 'Folder name', th: 'ชื่อโฟลเดอร์' },
  'folder.none': { en: 'No folder', th: 'ไม่อยู่ในโฟลเดอร์' },
  'folder.empty': { en: 'Empty — move places in here', th: 'ว่าง — ย้ายสถานที่มาที่นี่' },
  'folder.move': { en: 'Move to folder', th: 'ย้ายไปโฟลเดอร์' },
  'folder.moveTitle': { en: 'Move “{name}” to', th: 'ย้าย “{name}” ไปที่' },
  'folder.delete': { en: 'Delete folder', th: 'ลบโฟลเดอร์' },
  /* Says plainly that the places survive - it looks like it deletes them. */
  'folder.deleteConfirm': {
    en: 'Delete the folder “{name}”?\n\nThe places inside it are kept — they just become unfiled.',
    th: 'ลบโฟลเดอร์ “{name}” ใช่ไหม?\n\nสถานที่ข้างในจะไม่ถูกลบ เพียงแต่จะไม่อยู่ในโฟลเดอร์',
  },

  /* AI-assisted search: offered under the ordinary results, never instead. */
  'search.ai': { en: 'Ask AI to find it', th: 'ให้ AI ช่วยค้นหา' },
  'search.aiSub': {
    en: 'Describe the place in your own words',
    th: 'อธิบายสถานที่ด้วยคำพูดของคุณเอง',
  },
  'search.aiThinking': { en: 'AI is looking…', th: 'AI กำลังค้นหา…' },
  'search.aiFound': { en: 'AI suggestions — check before you go', th: 'AI แนะนำ — ตรวจสอบก่อนออกเดินทาง' },
  'search.aiNothing': {
    en: 'AI could not place that either.',
    th: 'AI ก็หาสถานที่นี้ไม่พบเช่นกัน',
  },
  'search.aiFailed': { en: 'AI search failed. Try again.', th: 'ค้นหาด้วย AI ไม่สำเร็จ ลองอีกครั้ง' },
  'search.aiOff': {
    en: 'AI search is not set up on the server.',
    th: 'ยังไม่ได้ตั้งค่าการค้นหาด้วย AI บนเซิร์ฟเวอร์',
  },
  'search.aiLimit': {
    en: "Daily AI limit reached. It resets at midnight.",
    th: 'ใช้ AI ครบตามจำนวนของวันนี้แล้ว จะรีเซ็ตตอนเที่ยงคืน',
  },
  'search.offline': {
    en: 'You are offline - search needs a connection.',
    th: 'ออฟไลน์อยู่ - การค้นหาต้องใช้อินเทอร์เน็ต',
  },
  'search.empty': {
    en: 'Search for a place, paste coordinates or a Google Maps link, or long-press the map to drop a pin.',
    th: 'ค้นหาสถานที่ วางพิกัดหรือลิงก์ Google Maps หรือกดค้างบนแผนที่เพื่อปักหมุด',
  },
  'search.saved': { en: 'Saved places', th: 'สถานที่บันทึกไว้' },
  'search.recent': { en: 'Recent', th: 'ล่าสุด' },
  'search.by': { en: 'by {name}', th: 'โดย {name}' },
  'search.deleteFailed': { en: 'Could not delete.', th: 'ลบไม่สำเร็จ' },

  /* places */
  'place.droppedPin': { en: 'Dropped pin', th: 'หมุดที่ปัก' },
  'search.approx': { en: 'street not found - check this', th: 'ไม่พบซอยนี้ - โปรดตรวจสอบ' },
  'search.approxHint': {
    en: 'This soi is not in the map data. Paste a Google Maps link or coordinates, or long-press the map to drop a pin.',
    th: 'ไม่มีซอยนี้ในข้อมูลแผนที่ วางลิงก์ Google Maps หรือพิกัด หรือกดค้างบนแผนที่เพื่อปักหมุด',
  },
  'place.coordinates': { en: 'Coordinates', th: 'พิกัด' },

  /* traffic avoidance */
  'traffic.chip': { en: 'Avoid traffic', th: 'เลี่ยงรถติด' },
  'traffic.picked': {
    en: 'Route {letter}: least traffic, easy to drive',
    th: 'เส้นทาง {letter}: รถติดน้อยสุด ขับง่าย',
  },
  'traffic.already': {
    en: 'Already on the calmest route',
    th: 'อยู่บนเส้นทางที่ติดน้อยที่สุดแล้ว',
  },
  'traffic.ahead': {
    en: 'Heavy traffic ahead - an easier route saves ~{min} min',
    th: 'ข้างหน้ารถติดหนัก - เส้นทางที่ง่ายกว่าเร็วขึ้น ~{min} นาที',
  },
  'traffic.voice': {
    en: 'Heavy traffic ahead. An easier route would save about {min} minutes.',
    th: 'ข้างหน้ารถติดหนัก มีเส้นทางที่ง่ายกว่าซึ่งเร็วขึ้นประมาณ {min} นาที',
  },
  'traffic.switch': { en: 'Switch', th: 'เปลี่ยน' },
  'traffic.switched': { en: 'Rerouted around the traffic', th: 'เปลี่ยนเส้นทางเลี่ยงรถติดแล้ว' },
  'set.trafficWatch': { en: 'Warn about traffic ahead', th: 'เตือนรถติดข้างหน้า' },

  /* PTT spacing on route options */
  'ptt.gaps': {
    en: '{n}× PTT · avg gap {avg} · longest {max}',
    th: 'ปตท {n} แห่ง · ห่างเฉลี่ย {avg} · ไกลสุด {max}',
  },
  'ptt.one': { en: 'Only 1 PTT found on this route', th: 'พบ ปตท เพียง 1 แห่งบนเส้นทางนี้' },
  'ptt.none': { en: 'No PTT found along this route', th: 'ไม่พบ ปตท ตามเส้นทางนี้' },

  /* ferries */
  'ferry.settings': { en: 'Ferries', th: 'เรือเฟอร์รี่' },
  'ferry.settingsHint': {
    en: 'Departure times per operator. Everyone plans to be at the pier 1 hour before the sailing; the wait is included in the ETA.',
    th: 'เวลาออกเรือของแต่ละบริษัท ทุกคันต้องถึงท่าก่อนเรือออก 1 ชั่วโมง และเวลารอถูกรวมในเวลาถึงแล้ว',
  },
  'ferry.truckOk': { en: 'Takes the big truck', th: 'รับรถบรรทุกใหญ่' },
  'ferry.crossingMin': { en: 'Crossing time', th: 'เวลาข้าม' },
  'ferry.sailing': { en: '{name} {time}', th: '{name} {time}' },
  'ferry.beThere': { en: 'be at {pier} by {time}', th: 'ถึงท่า{pier}ภายใน {time}' },
  'ferry.nextDay': {
    en: "today's last ferry missed - this is tomorrow",
    th: 'ไม่ทันเรือเที่ยวสุดท้ายวันนี้ - เป็นของพรุ่งนี้',
  },
  'ferry.noVehicle': {
    en: 'No {name} ferry takes this vehicle - check Settings > Ferries',
    th: 'ไม่มีเรือ{name}ที่รับรถคันนี้ - ดูตั้งค่า > เรือเฟอร์รี่',
  },
  'place.mapsLink': { en: 'From Google Maps link', th: 'จากลิงก์ Google Maps' },
  'place.savedBy': { en: 'Saved by {name}', th: 'บันทึกโดย {name}' },
  'place.saveFailed': { en: 'Could not save.', th: 'บันทึกไม่สำเร็จ' },

  /* save dialog + categories */
  'save.title': { en: 'Save place for the fleet', th: 'บันทึกสถานที่ให้ทีม' },
  'save.name.ph': { en: 'Name this place', th: 'ตั้งชื่อสถานที่' },
  'save.save': { en: 'Save', th: 'บันทึก' },
  'cat.home': { en: 'Home', th: 'บ้าน' },
  'cat.work': { en: 'Work', th: 'ที่ทำงาน' },
  'cat.showroom': { en: 'Showroom', th: 'โชว์รูม' },
  'cat.depot': { en: 'Depot', th: 'คลังสินค้า' },
  'cat.other': { en: 'Other', th: 'อื่นๆ' },

  /* preview */
  'preview.finding': { en: 'Finding routes…', th: 'กำลังหาเส้นทาง…' },
  'preview.waitingFix': { en: 'Waiting for a GPS fix…', th: 'กำลังรอสัญญาณ GPS…' },
  'preview.traffic': { en: '+{d} traffic', th: 'รถติด +{d}' },
  'preview.clear': { en: 'traffic clear', th: 'การจราจรคล่องตัว' },
  'preview.eta': { en: 'ETA {t}', th: 'ถึง {t}' },
  'preview.alternative': { en: 'Alternative route', th: 'เส้นทางสำรอง' },
  'route.fastest': { en: 'Fastest', th: 'เร็วที่สุด' },
  'route.no-tolls': { en: 'No tolls', th: 'ไม่ใช้ทางด่วน' },
  'route.no-motorway': { en: 'No motorway', th: 'เลี่ยงมอเตอร์เวย์' },
  'route.usual': { en: 'Your usual way', th: 'เส้นทางที่คุณใช้ประจำ' },
  'preview.avoidTolls': { en: 'Avoid tolls', th: 'เลี่ยงทางด่วน' },
  'preview.avoidMotorways': { en: 'Avoid motorways', th: 'เลี่ยงมอเตอร์เวย์' },
  'preview.from': { en: 'From', th: 'จาก' },
  'preview.here': { en: 'Current location', th: 'ตำแหน่งปัจจุบัน' },
  /* No GPS: the trip is still plannable, it just needs a start point naming. */
  'preview.setStart': { en: 'Tap to set a starting point', th: 'แตะเพื่อเลือกจุดเริ่มต้น' },
  'preview.noFixSetStart': {
    en: 'No GPS signal here. Set a starting point above to plan the route.',
    th: 'ไม่มีสัญญาณ GPS ที่นี่ เลือกจุดเริ่มต้นด้านบนเพื่อวางแผนเส้นทาง',
  },
  'preview.reset': { en: 'reset', th: 'ล้าง' },
  'preview.cancel': { en: 'Cancel', th: 'ยกเลิก' },
  'preview.start': { en: 'Start', th: 'เริ่มนำทาง' },

  /* navigation */
  'nav.then': { en: 'then', th: 'จากนั้น' },
  'nav.rerouting': { en: 'Rerouting…', th: 'กำลังหาเส้นทางใหม่…' },
  'nav.rerouteFailed': { en: 'Rerouting failed - retrying…', th: 'หาเส้นทางใหม่ไม่สำเร็จ - กำลังลองใหม่…' },
  'nav.rerouteOffline': {
    en: 'Offline - continue to rejoin the route',
    th: 'ออฟไลน์ - ขับต่อเพื่อกลับเข้าเส้นทาง',
  },
  'nav.arrivedToast': { en: 'You have arrived', th: 'ถึงที่หมายแล้ว' },
  'nav.recenter': { en: 'Re-centre', th: 'กลับสู่ตำแหน่ง' },
  'nav.voice.starting': { en: 'Starting navigation to {name}.', th: 'เริ่มนำทางไป {name}' },
  'nav.voice.updated': { en: 'Route updated.', th: 'ปรับเส้นทางแล้ว' },
  'nav.voice.arrived': { en: 'You have arrived at {name}.', th: 'ถึง {name} แล้ว' },

  /* durations */
  'dur.lessMin': { en: 'less than a min', th: 'ไม่ถึงนาที' },
  'dur.min': { en: '{n} min', th: '{n} นาที' },
  'dur.hr': { en: '{n} hr', th: '{n} ชม.' },
  'dur.hrMin': { en: '{h} hr {m} min', th: '{h} ชม. {m} นาที' },

  /* maneuver sign */
  'sign.on': { en: 'On', th: 'อยู่บน' },
  'sign.then': { en: 'Next', th: 'ถัดไป' },

  /* route stats + AI advisor */
  'stats.signals': { en: '{n} lights', th: 'ไฟแดง {n} จุด' },
  'stats.turns': { en: '{n} turns', th: 'เลี้ยว {n} ครั้ง' },
  'stats.tolls': { en: '{n} tolls', th: 'ด่าน {n} จุด' },
  'stats.calmest': { en: 'Fewest lights', th: 'ไฟแดงน้อยสุด' },
  'stats.atLights': { en: '{d} at lights', th: 'รอไฟแดง {d}' },
  'ai.thinking': { en: 'Checking your past trips…', th: 'กำลังตรวจเส้นทางที่เคยไป…' },
  'ai.badge': { en: 'AI suggestion', th: 'คำแนะนำจาก AI' },
  'ai.use': { en: 'Use this route', th: 'ใช้เส้นทางนี้' },
  'ai.dismiss': { en: 'Dismiss', th: 'ปิด' },

  /* fleet + quick messages
     These are the ONLY place the three questions exist as words. The network
     carries 'food' | 'fuel' | 'help', so each driver reads and hears them in
     their own language regardless of who sent them. */
  'fleet.title': { en: 'Fleet', th: 'ทีมขับรถ' },
  'fleet.nobody': { en: 'No other drivers are out right now.', th: 'ยังไม่มีคนขับคนอื่นออกรถตอนนี้' },
  'fleet.stale': { en: 'no signal', th: 'ไม่มีสัญญาณ' },
  'fleet.ask': { en: 'Ask {name}', th: 'ถาม {name}' },
  'msg.food': { en: 'Food?', th: 'แวะกินข้าวไหม?' },
  'msg.fuel': { en: 'Fuel stop?', th: 'แวะเติมน้ำมันไหม?' },
  'msg.help': { en: 'Help!', th: 'ช่วยด้วย!' },
  'msg.yes': { en: 'Yes', th: 'ตกลง' },
  'msg.no': { en: 'No', th: 'ไม่' },
  'msg.sent': { en: 'Sent to {name}', th: 'ส่งถึง {name} แล้ว' },
  /* Acknowledges an answer that has come back; it waits until this is tapped. */
  'msg.ok': { en: 'OK', th: 'รับทราบ' },
  'msg.answered': { en: 'answered', th: 'ตอบว่า' },
  'msg.sendFailed': { en: 'Could not send.', th: 'ส่งไม่สำเร็จ' },
  /* Spoken aloud on arrival, in THIS driver's language and voice. */
  'msg.incoming.voice': { en: '{name} asks: {what}', th: '{name} ถามว่า {what}' },
  'msg.reply.voice': { en: '{name} says {answer}', th: '{name} ตอบว่า {answer}' },
  'msg.reply.toast': { en: '{name}: {answer}', th: '{name}: {answer}' },

  /* road surface (learned from our own drives) */
  'rough.title': { en: 'Rough road', th: 'ถนนขรุขระ' },
  'rough.onRoute': {
    en: '{d} of rough surface on this route',
    th: 'ผิวถนนขรุขระ {d} บนเส้นทางนี้',
  },
  'rough.bad': { en: 'badly broken in places', th: 'ชำรุดหนักบางช่วง' },
  'rough.moderate': { en: 'uneven in places', th: 'ไม่เรียบบางช่วง' },
  'rough.aheadVoice': { en: 'Rough road ahead.', th: 'ข้างหน้าถนนขรุขระ' },
  'rough.aheadToast': { en: 'Rough road in {d}', th: 'ถนนขรุขระในอีก {d}' },
  'set.overlay': { en: 'Roads we have driven', th: 'ถนนที่เราเคยขับ' },
  'set.overlay.off': { en: 'Hidden', th: 'ซ่อน' },
  'set.overlay.speed': { en: 'Colour by speed', th: 'สีตามความเร็ว' },
  'set.overlay.rough': { en: 'Colour by roughness', th: 'สีตามความขรุขระ' },
  'set.learnSpeeds': { en: 'Use my measured speeds', th: 'ใช้ความเร็วที่วัดได้' },
  'set.measured': { en: 'measured {n} km/h', th: 'วัดได้ {n} กม./ชม.' },

  /* route info panel */
  'info.title': { en: 'The route ahead', th: 'เส้นทางข้างหน้า' },
  'info.ahead': { en: 'Ahead', th: 'ข้างหน้า' },
  'info.remaining': { en: 'Distance left', th: 'ระยะทางที่เหลือ' },
  'info.timeLeft': { en: 'Time left', th: 'เวลาที่เหลือ' },
  'info.arrival': { en: 'Arriving', th: 'ถึงเวลา' },
  'info.time': { en: 'Where the time goes', th: 'เวลาหมดไปกับอะไร' },
  'info.driving': { en: 'Driving', th: 'ขับรถ' },
  'info.atLights': { en: 'Waiting at lights', th: 'รอไฟแดง' },
  'info.routerSaid': { en: "Map's own estimate", th: 'ค่าประมาณของแผนที่' },
  'info.terrain': { en: 'Terrain', th: 'ภูมิประเทศ' },
  'info.climb': { en: 'Total climb', th: 'ไต่ระดับรวม' },
  'info.descent': { en: 'Total descent', th: 'ลงระดับรวม' },
  'info.highest': { en: 'Highest point', th: 'จุดสูงสุด' },
  'info.steepestUp': { en: 'Steepest climb', th: 'ทางขึ้นชันสุด' },
  'info.steepestDown': { en: 'Steepest descent', th: 'ทางลงชันสุด' },
  'info.tooSteep': {
    en: 'Too steep for the {name} when loaded - check before committing.',
    th: 'ชันเกินไปสำหรับ {name} เมื่อบรรทุกของ - ตรวจสอบก่อนตัดสินใจ',
  },
  'info.brakeCare': {
    en: '{pct}% over {km} km - use a low gear and watch brake heat.',
    th: '{pct}% ยาว {km} กม. - ใช้เกียร์ต่ำ ระวังเบรกร้อน',
  },
  'info.roads': { en: 'Roads', th: 'ประเภทถนน' },
  'info.stops': { en: 'What will stop you', th: 'สิ่งที่ทำให้ต้องหยุด' },
  'info.signals': { en: 'Traffic lights', th: 'ไฟจราจร' },
  'info.tolls': { en: 'Toll booths', th: 'ด่านเก็บเงิน' },
  'info.crossings': { en: 'Level crossings', th: 'จุดตัดทางรถไฟ' },
  'info.turns': { en: 'Turns', th: 'จุดเลี้ยว' },
  'info.nextFuel': { en: 'Next PTT', th: 'ปตท. ถัดไป' },

  /* gradients */
  'grade.climbFor': { en: '{pct}% climb for {km} km', th: 'ทางขึ้น {pct}% ยาว {km} กม.' },
  'grade.descentFor': { en: '{pct}% descent for {km} km', th: 'ทางลง {pct}% ยาว {km} กม.' },
  'grade.hard': {
    en: 'Hard work for this vehicle - low gear, and watch the brakes coming down.',
    th: 'หนักสำหรับรถคันนี้ - ใช้เกียร์ต่ำ และระวังเบรกตอนลง',
  },
  'grade.blocking': {
    en: 'This is beyond what a loaded vehicle of this type can climb. Check before committing.',
    th: 'ชันเกินกว่าที่รถบรรทุกที่มีน้ำหนักจะขึ้นได้ ตรวจสอบก่อนตัดสินใจ',
  },
  'grade.ascent': { en: ' Total climb {m} m.', th: ' ไต่ระดับรวม {m} ม.' },

  /* PTT fuel stops */
  'fuel.left': { en: 'left side', th: 'ฝั่งซ้าย' },
  'fuel.right': { en: 'right side', th: 'ฝั่งขวา' },
  'route.big-roads': { en: 'BIG ROADS', th: 'ถนนใหญ่' },
  'width.big': { en: '{p}% dual carriageway or better', th: 'ถนนคู่ขึ้นไป {p}%' },
  'width.allBig': { en: 'Dual carriageway the whole way', th: 'ถนนคู่ตลอดสาย' },
  'width.motorway': { en: '{p}% motorway', th: 'มอเตอร์เวย์ {p}%' },
  'width.smallRun': { en: '{d} single lane', th: 'เลนเดียว {d}' },
  'set.aiHint': {
    en: 'A second opinion when several routes are offered. Off by default - the signal counts and road widths usually answer it already.',
    th: 'ความเห็นที่สองเมื่อมีหลายเส้นทาง ปิดไว้เป็นค่าเริ่มต้น เพราะข้อมูลไฟแดงและขนาดถนนมักตอบได้อยู่แล้ว',
  },
  'set.clock24': { en: '24-hour clock', th: 'เวลาแบบ 24 ชั่วโมง' },
  'sound.title': { en: 'Sound', th: 'เสียง' },
  'sound.voice': { en: 'Voice', th: 'เสียงพูด' },
  'sound.voiceHint': { en: 'Spoken instructions', th: 'บอกทางด้วยเสียง' },
  'sound.alerts': { en: 'Alerts only', th: 'เฉพาะเสียงเตือน' },
  'sound.alertsHint': { en: 'A chime when something needs doing', th: 'เสียงเตือนเมื่อต้องทำอะไร' },
  'sound.muted': { en: 'Muted', th: 'ปิดเสียง' },
  'sound.mutedHint': { en: 'No sound at all', th: 'ไม่มีเสียงเลย' },
  'sound.volume': { en: 'Voice volume', th: 'ระดับเสียงพูด' },
  'sound.test': { en: 'Test', th: 'ทดสอบ' },
  'rough.sensitivity': { en: 'Rough-road sensitivity', th: 'ความไวถนนขรุขระ' },
  'rough.sens.1': { en: 'Only broken roads', th: 'เฉพาะถนนพังจริง' },
  'rough.sens.2': { en: 'Bad roads', th: 'ถนนแย่' },
  'rough.sens.3': { en: 'Normal', th: 'ปานกลาง' },
  'rough.sens.4': { en: 'Sensitive', th: 'ไว' },
  'rough.sens.5': { en: 'Anything bumpy', th: 'ขรุขระนิดหน่อยก็เตือน' },
  'rough.avoid': { en: 'Steer away from rough roads', th: 'เลี่ยงถนนขรุขระ' },
  'rough.avoidHint': {
    en: 'Rough surfaces count against a route, not just warn about it',
    th: 'ถนนขรุขระจะถูกหักคะแนนเส้นทาง ไม่ใช่แค่เตือน',
  },
  'nav.backToStart': { en: 'Back to start', th: 'กลับจุดเริ่มต้น' },
  'nav.backToStartHint': { en: 'Route back to where this trip began', th: 'นำทางกลับจุดที่เริ่มเดินทาง' },
  'fuel.close': { en: 'Close', th: 'ปิด' },
  'fuel.confirm': { en: 'Route via this station?', th: 'ให้แวะปั๊มนี้ไหม?' },
  'fuel.choose': { en: 'Which station?', th: 'เลือกปั๊มไหน?' },
  'fuel.frontage': { en: 'on the frontage road', th: 'อยู่บนทางคู่ขนาน' },
  'fuel.frontageNote': {
    en: 'Turn off before the forecourt - you cannot enter from the main road.',
    th: 'ต้องเลี้ยวออกก่อนถึงปั๊ม เข้าจากถนนหลักไม่ได้',
  },
  'fuel.next': { en: 'Next one instead', th: 'ปั๊มถัดไปแทน' },
  'fuel.go': { en: 'Yes, go there', th: 'ไปที่นั่น' },
  'fuel.detour': { en: 'Detour to {name}', th: 'แวะ {name}' },
  'fuel.undo': { en: 'Undo', th: 'ยกเลิก' },
  'fuel.restored': { en: 'Back on the original route', th: 'กลับสู่เส้นทางเดิม' },
  'fuel.failed': { en: 'Could not route via that station.', th: 'ไม่สามารถวางเส้นทางผ่านปั๊มนี้ได้' },

  /* settings */
  'set.title': { en: 'Settings', th: 'ตั้งค่า' },
  'set.vehicle': { en: 'Vehicle', th: 'ยานพาหนะ' },
  'set.vehicle.car': { en: 'Car', th: 'รถยนต์' },
  'set.vehicle.truck': { en: 'Truck - uses size limits', th: 'รถบรรทุก - ใช้ขนาดจำกัดเส้นทาง' },
  'set.vehicle.addCar': { en: '+ Add vehicle', th: '+ เพิ่มรถ' },
  'set.vehicle.addTruck': { en: '+ Add truck', th: '+ เพิ่มรถบรรทุก' },
  'set.vehicle.edit': { en: 'Edit vehicle', th: 'แก้ไขรถ' },
  'set.vehicle.name': { en: 'Name', th: 'ชื่อ' },
  'set.vehicle.kind': { en: 'Type', th: 'ประเภท' },
  'set.vehicle.delete': { en: 'Delete vehicle', th: 'ลบรถ' },
  'set.vehicle.unnamed': { en: 'Vehicle', th: 'ยานพาหนะ' },
  'set.ai': { en: 'AI route advice', th: 'คำแนะนำเส้นทางจาก AI' },
  'vehicle.choose': { en: 'Which vehicle?', th: 'ใช้รถคันไหน?' },
  'set.easyPass': { en: 'I have Easy Pass / M-Pass', th: 'มี Easy Pass / M-Pass' },
  'live.title': { en: 'Live data', th: 'ข้อมูลสด' },
  'live.speed': { en: 'Speed', th: 'ความเร็ว' },
  'live.roughness': { en: 'Road surface', th: 'ผิวถนน' },
  'live.noSensor': { en: 'no sensor', th: 'ไม่มีเซ็นเซอร์' },
  'live.rough.smooth': { en: 'smooth', th: 'เรียบ' },
  'live.rough.fair': { en: 'fair', th: 'พอใช้' },
  'live.rough.rough': { en: 'rough', th: 'ขรุขระ' },
  'live.rough.bad': { en: 'very bad', th: 'แย่มาก' },
  'live.altitude': { en: 'Altitude', th: 'ความสูง' },
  'live.accuracy': { en: 'GPS accuracy', th: 'ความแม่นยำ GPS' },
  'live.heading': { en: 'Heading', th: 'ทิศทาง' },
  'live.gradeNow': { en: 'Gradient here', th: 'ความชันตรงนี้' },
  'live.roadClass': { en: 'Road class', th: 'ประเภทถนน' },
  'live.tier.motorway': { en: 'Motorway', th: 'มอเตอร์เวย์' },
  'live.tier.dual': { en: 'Dual carriageway', th: 'ถนนคู่' },
  'live.tier.single': { en: 'Single lane', th: 'ถนนเลนเดียว' },
  'live.driven': { en: 'Driven', th: 'ขับไปแล้ว' },
  'live.moving': { en: 'Moving', th: 'เคลื่อนที่' },
  'live.samples': { en: 'Samples logged', th: 'จุดที่บันทึก' },
  'live.toTurn': { en: 'To next turn', th: 'ถึงจุดเลี้ยว' },
  'live.offRoute': { en: 'Off route by', th: 'ห่างเส้นทาง' },
  'live.note': {
    en: 'Recorded on every drive and used to improve future routes.',
    th: 'บันทึกทุกครั้งที่ขับ เพื่อปรับปรุงเส้นทางในอนาคต',
  },
  'set.pace': { en: 'Your top speeds', th: 'ความเร็วสูงสุดที่คุณขับ' },
  'set.pace.hint': {
    en: 'The fastest you ever drive on each kind of road - not your average. Journey times never assume you go faster than this, or faster than the traffic ahead.',
    th: 'ความเร็วสูงสุดที่คุณขับบนถนนแต่ละแบบ ไม่ใช่ค่าเฉลี่ย เวลาเดินทางจะไม่คิดว่าคุณขับเร็วกว่านี้ หรือเร็วกว่าสภาพจราจรข้างหน้า',
  },
  'set.pace.motorway': { en: 'Motorway (120 km/h limit)', th: 'มอเตอร์เวย์ (จำกัด 120 กม./ชม.)' },
  'set.pace.dual': { en: 'Dual carriageway (2+ lanes)', th: 'ถนนคู่ (2 ช่องขึ้นไป)' },
  'set.pace.single': { en: 'Single-lane road / soi', th: 'ถนนเลนเดียว / ซอย' },
  'set.pace.signal': {
    en: 'Time lost per red light (shown on routes, not added to the ETA)',
    th: 'เวลาที่เสียต่อไฟแดง (แสดงบนเส้นทาง ไม่รวมในเวลาถึง)',
  },
  'set.route': { en: 'Route options', th: 'ตัวเลือกเส้นทาง' },
  'set.avoidFerries': { en: 'Avoid ferries', th: 'เลี่ยงเรือข้ามฟาก' },
  'set.avoidUnpaved': { en: 'Avoid unpaved roads', th: 'เลี่ยงถนนลูกรัง' },
  'set.useTraffic': { en: 'Live traffic routing', th: 'ใช้ข้อมูลจราจรสด' },
  'set.alternatives': { en: 'Offer alternative routes', th: 'เสนอเส้นทางสำรอง' },
  'set.guidance': { en: 'Guidance', th: 'การนำทาง' },
  'set.voice': { en: 'Voice guidance', th: 'เสียงนำทาง' },
  'set.duck': { en: 'Lower music while speaking', th: 'ลดเสียงเพลงขณะพูด' },
  'set.audioSource': { en: 'Media app', th: 'แอปสื่อ' },
  'set.audioSourceHint': {
    en: 'Audible has no public API, so its bar opens the Audible app - playback is controlled there or with the car controls.',
    th: 'Audible ไม่มี API สาธารณะ แถบของมันจึงเปิดแอป Audible - ควบคุมการเล่นในแอปหรือจากปุ่มในรถ',
  },
  'set.autozoom': { en: 'Auto-zoom into junctions', th: 'ซูมเข้าทางแยกอัตโนมัติ' },
  'set.awake': { en: 'Keep screen on', th: 'เปิดหน้าจอค้างไว้' },
  'set.overspeed': { en: 'Overspeed warning', th: 'เตือนความเร็วเกิน' },
  'set.overspeed.off': { en: 'Off', th: 'ปิด' },
  'set.display': { en: 'Display', th: 'การแสดงผล' },
  'set.language': { en: 'Language', th: 'ภาษา' },
  'set.theme': { en: 'Map theme', th: 'ธีมแผนที่' },
  'set.theme.auto': { en: 'Auto (sun-based)', th: 'อัตโนมัติ (ตามดวงอาทิตย์)' },
  'set.theme.night': { en: 'Night', th: 'กลางคืน' },
  'set.theme.day': { en: 'Day', th: 'กลางวัน' },
  'set.units': { en: 'Units', th: 'หน่วย' },
  'set.units.km': { en: 'Kilometres', th: 'กิโลเมตร' },
  'set.units.mi': { en: 'Miles', th: 'ไมล์' },
  'set.offline': { en: 'Cache map for offline use', th: 'บันทึกแผนที่ใช้ออฟไลน์' },
  'set.logTrips': { en: 'Record trip history', th: 'บันทึกประวัติการเดินทาง' },
  'set.admin': { en: 'Users (admin)', th: 'ผู้ใช้ (แอดมิน)' },
  'set.admin.loading': { en: 'Loading users…', th: 'กำลังโหลดผู้ใช้…' },
  'set.admin.none': { en: 'No other users yet.', th: 'ยังไม่มีผู้ใช้อื่น' },
  'set.admin.approve': { en: 'Approve', th: 'อนุมัติ' },
  'set.admin.disable': { en: 'Disable', th: 'ระงับ' },
  'set.admin.enable': { en: 'Re-enable', th: 'เปิดใช้อีกครั้ง' },
  'set.admin.resetPin': { en: 'Reset PIN', th: 'รีเซ็ต PIN' },
  'set.admin.newPin': { en: 'New PIN for this user (4-10 digits):', th: 'PIN ใหม่ของผู้ใช้นี้ (4-10 หลัก):' },
  'set.status.pending': { en: 'pending', th: 'รออนุมัติ' },
  'set.status.approved': { en: 'approved', th: 'อนุมัติแล้ว' },
  'set.status.disabled': { en: 'disabled', th: 'ถูกระงับ' },
  'set.account': { en: 'Account', th: 'บัญชี' },
  'set.signedInAs': { en: 'Signed in as', th: 'เข้าสู่ระบบเป็น' },
  'set.admin.tag': { en: ' (admin)', th: ' (แอดมิน)' },
  'set.signOut': { en: 'Sign out', th: 'ออกจากระบบ' },
  'set.failed': { en: 'Failed', th: 'ไม่สำเร็จ' },

  /* trips */
  'trips.title': { en: 'Trips', th: 'ประวัติการเดินทาง' },
  'trips.loading': { en: 'Loading…', th: 'กำลังโหลด…' },
  'trips.empty': {
    en: "No trips yet. Drive somewhere and it'll be logged here.",
    th: 'ยังไม่มีการเดินทาง ขับไปที่ไหนสักแห่งแล้วจะบันทึกไว้ที่นี่',
  },
  'trips.again': { en: 'Navigate again', th: 'นำทางอีกครั้ง' },
  'trips.delete': { en: 'Delete', th: 'ลบ' },
  'trips.details': { en: 'Details', th: 'รายละเอียด' },
  'trips.map': { en: 'Map', th: 'แผนที่' },
  'share.title': { en: 'Save this location as…', th: 'บันทึกตำแหน่งนี้เป็น…' },
  'place.rename': { en: 'Rename place', th: 'เปลี่ยนชื่อสถานที่' },
  'trips.goHere': { en: 'Navigate here', th: 'นำทางไปที่นี่' },
  'trips.viewing': { en: 'Viewing trip', th: 'กำลังดูการเดินทาง' },
  'set.crumbs': { en: 'Dotted trail of last 100 km', th: 'เส้นประ 100 กม. ล่าสุดที่ขับ' },
  'set.buildings3d': { en: '3D buildings', th: 'อาคาร 3 มิติ' },
  'set.pitch': { en: '3D view angle', th: 'มุมเอียงมุมมอง 3 มิติ' },
  'set.driveZoom': { en: 'Driving zoom', th: 'ระยะซูมขณะขับ' },
  'set.driveZoom.auto': { en: 'Auto', th: 'อัตโนมัติ' },
  'set.driveZoomHint': {
    en: 'Auto zooms out as you speed up. A fixed level holds that zoom - junctions still zoom in as you approach.',
    th: 'อัตโนมัติจะซูมออกเมื่อขับเร็วขึ้น ระดับคงที่จะคงซูมไว้ - แต่ยังซูมเข้าเมื่อใกล้ทางแยกเช่นเดิม',
  },
  'health.wheel': {
    en: 'Vehicle check: vibration at wheel speed has risen over recent trips. Worth checking wheel balance and tyres.',
    th: 'ตรวจสอบรถ: แรงสั่นตามรอบล้อเพิ่มขึ้นในช่วงหลัง ควรตรวจถ่วงล้อและยาง',
  },
  'health.damper': {
    en: 'Vehicle check: the body keeps bouncing after bumps more than it used to. Worth checking the shock absorbers.',
    th: 'ตรวจสอบรถ: ตัวรถเด้งค้างหลังผ่านหลุมมากกว่าเดิม ควรตรวจโช้คอัพ',
  },
  'health.vib': {
    en: 'Vehicle check: overall vibration has been rising on recent trips, even on familiar roads. Worth a look at the next service.',
    th: 'ตรวจสอบรถ: แรงสั่นโดยรวมเพิ่มขึ้นในช่วงหลัง แม้บนถนนเดิม ควรให้ช่างดูในการเข้าศูนย์ครั้งถัดไป',
  },
  'trips.avgSpeed': { en: 'Avg', th: 'เฉลี่ย' },
  'trips.maxSpeed': { en: 'Max', th: 'สูงสุด' },
  'trips.moving': { en: 'Moving', th: 'เคลื่อนที่' },
  'trips.stopped': { en: 'Stopped', th: 'หยุดนิ่ง' },
  'trips.fuel': { en: 'Fuel est.', th: 'น้ำมันโดยประมาณ' },
  'trips.vsEstimate': { en: 'vs estimate', th: 'เทียบกับที่คาด' },
  'trips.reroutes': { en: '{n} reroutes', th: 'หาเส้นทางใหม่ {n} ครั้ง' },
  'trips.noDetail': {
    en: 'No detail was recorded for this trip.',
    th: 'ไม่มีรายละเอียดที่บันทึกไว้สำหรับทริปนี้',
  },
  'trips.segments': { en: 'Along the way', th: 'ระหว่างทาง' },
  'trips.unknownRoad': { en: 'unnamed road', th: 'ถนนไม่ระบุชื่อ' },

  /* spotify */
  'sp.connect': { en: 'Connect Spotify', th: 'เชื่อมต่อ Spotify' },
  'sp.nothing': { en: 'Nothing playing', th: 'ไม่มีเพลงเล่นอยู่' },
  'sp.on': { en: 'on {device}', th: 'ที่ {device}' },
  'sp.hint': { en: 'Open Spotify or press play', th: 'เปิด Spotify หรือกดเล่น' },
  'aud.hint': { en: 'Audiobooks in the Audible app', th: 'หนังสือเสียงในแอป Audible' },
  'aud.open': { en: 'Open Audible', th: 'เปิด Audible' },
  'sp.playHere': { en: 'Play through this device', th: 'เล่นผ่านอุปกรณ์นี้' },
  'sp.playingHere': { en: 'Playing here', th: 'กำลังเล่นที่นี่' },

  /* gps errors */
  'gps.denied': {
    en: 'Location permission denied. Allow location access for this site in your browser settings.',
    th: 'ไม่ได้รับสิทธิ์ตำแหน่ง กรุณาอนุญาตการเข้าถึงตำแหน่งในตั้งค่าเบราว์เซอร์',
  },
  'gps.unavailable': {
    en: 'Location unavailable. Move to open sky and try again.',
    th: 'ไม่พบตำแหน่ง ลองย้ายไปที่โล่งแจ้ง',
  },
  'gps.waiting': { en: 'Waiting for a GPS fix…', th: 'กำลังรอสัญญาณ GPS…' },
  'gps.error': { en: 'Location error.', th: 'ข้อผิดพลาดตำแหน่ง' },
  'gps.none': { en: 'This device has no location support.', th: 'อุปกรณ์นี้ไม่รองรับตำแหน่ง' },
};

let lang: Lang = loadSettings().language ?? 'en';

export function setLang(l: Lang): void {
  lang = l;
}

export function getLang(): Lang {
  return lang;
}

export function t(key: string, params?: Record<string, string | number>): string {
  const entry = STRINGS[key];
  let s = entry ? entry[lang] : key;
  if (params) {
    for (const [k, v] of Object.entries(params)) {
      s = s.replace(`{${k}}`, String(v));
    }
  }
  return s;
}
